'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCheck, Search } from 'lucide-react'

import type { NotificationView } from '@package/notification'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { ShareContentCover } from './share-content-dialog'
import { useSharedTRPC } from '../context'
import { CoverPreview } from './share-content-dialog'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShareRecipientReadStatus {
  id: string
  name: string
  image?: string | null
  /** Row subtitle. `null`/absent renders "Tanpa kelas", matching `ShareRecipientOption`. */
  schoolClassName?: string | null
  isRead: boolean
}

export type ShareRecipientsDialogState = 'ready' | 'loading' | 'error'

export interface ShareRecipientsDialogBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Same union the share dialog takes, so a book, a collection and a lesson module are
   * sized identically in both. Absent renders no cover rather than a placeholder frame.
   */
  cover?: ShareContentCover
  recipientCount: number
  createdAt: Date
  state: ShareRecipientsDialogState
  recipients: ShareRecipientReadStatus[]
  search: string
  onSearchChange: (search: string) => void
  onRetry?: () => void
}

// ---------------------------------------------------------------------------
// Internal pieces
// ---------------------------------------------------------------------------

/**
 * Rebuilds the share dialog's cover union from what the notification snapshot froze.
 *
 * `COLLECTION` rebuilds the 2x2 gallery from the member covers `share.create` snapshots
 * (`resolveEntitySnapshot`). Shares emitted before that snapshot existed only carry the
 * first member cover, which the gallery draws as a single tile. An unknown entity type
 * falls back to a portrait book cover — better than no cover.
 */
function coverFromNotification(notification: NotificationView): ShareContentCover | undefined {
  const { thumbnailUrl: coverUrl, thumbnailUrls } = notification.snapshot
  const entity = notification.entity

  // `LessonModuleCard` derives its placeholder artwork from the id, so it renders with
  // or without a cover URL — but it does need that id.
  if (entity && (entity.type === 'CHALLENGE' || entity.type === 'EXERCISE')) {
    return { kind: 'lessonModule', id: entity.id, coverUrl: coverUrl ?? null }
  }

  if (entity?.type === 'COLLECTION') {
    const urls = thumbnailUrls?.length ? thumbnailUrls : coverUrl ? [coverUrl] : []
    // The URL doubles as the key: `BookGalleryCard` only needs a stable id per tile, and
    // the snapshot froze covers, not book ids.
    return { kind: 'collection', books: urls.map((url) => ({ book: { id: url, coverUrl: url } })) }
  }

  if (!coverUrl) return undefined
  if (entity?.type === 'BOOK_COLLECTION') return { kind: 'bookCollection', coverUrl }
  return { kind: 'book', coverUrl }
}

/** "12 Januari 2026; 24:59" — same construction as `ChallengeCard`. */
function formatSharedAt(date: Date): string {
  const datePart = date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
  const timePart = date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${datePart}; ${timePart}`
}

function RecipientReadRow({ recipient }: { recipient: ShareRecipientReadStatus }) {
  const { isRead } = recipient

  return (
    <div className='srd:border-outline-variant srd:flex srd:w-full srd:min-w-0 srd:items-center srd:justify-between srd:gap-x-4 srd:border-b srd:px-1 srd:py-3'>
      <div className='srd:flex srd:w-full srd:min-w-0 srd:items-center srd:gap-x-3'>
        <Avatar profile='user' className='srd:size-10 srd:shrink-0'>
          <AvatarImage src={recipient.image ?? undefined} alt='' />
          <AvatarFallback>{getInitials(recipient.name)}</AvatarFallback>
        </Avatar>

        <div className='srd:flex srd:min-w-0 srd:flex-col'>
          <p
            className={cn(
              typographyVariants({ variant: 'title-sm' }),
              'srd:truncate',
              isRead ? 'srd:text-neutral-black' : 'srd:text-grey-50',
            )}
          >
            {recipient.name}
          </p>
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:truncate')}>
            {recipient.schoolClassName ?? 'Tanpa kelas'}
          </p>
        </div>
      </div>

      {/* Colour is the only other signal, so the state is spelled out for screen readers. */}
      <CheckCheck
        className={cn('srd:size-5 srd:shrink-0', isRead ? 'srd:text-blue-100' : 'srd:text-grey-40')}
        aria-label={isRead ? 'Sudah dibaca' : 'Belum dibaca'}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Presentational base — no data fetching, safe to use in Storybook
// ---------------------------------------------------------------------------

export function ShareRecipientsDialogBase({
  open,
  onOpenChange,
  cover,
  recipientCount,
  createdAt,
  state,
  recipients,
  search,
  onSearchChange,
  onRetry,
}: ShareRecipientsDialogBaseProps) {
  // Client-side for the same reason as the share dialog: the whole recipient list is
  // already in memory, so a round trip per keystroke would buy nothing.
  const filtered = search
    ? recipients.filter((recipient) => recipient.name.toLowerCase().includes(search.toLowerCase()))
    : recipients

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Everything except the recipient list is `shrink-0`, so the list is the only part
        that gives up space when the dialog hits its height cap — see the same note in
        `share-content-dialog.tsx`.
      */}
      <DialogContent className='srd:flex srd:max-h-[90dvh] srd:w-full srd:max-w-125 srd:flex-col srd:overflow-y-auto srd:rounded-2xl'>
        <DialogHeader className='srd:flex srd:shrink-0 srd:flex-col srd:items-center srd:gap-y-2'>
          {cover && <CoverPreview cover={cover} />}

          <DialogTitle className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>
            {`Dibagikan ke ${recipientCount} penerima`}
          </DialogTitle>
          <DialogDescription className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
            {formatSharedAt(createdAt)}
          </DialogDescription>
        </DialogHeader>

        {state === 'error' ? (
          <div className='srd:flex srd:min-h-0 srd:flex-1 srd:flex-col srd:items-center srd:gap-y-2 srd:overflow-y-auto srd:py-10 srd:text-center'>
            <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
              Oops, Terjadi Kesalahan
            </p>
            <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
              Gagal memuat daftar penerima.
            </p>
            {onRetry && (
              <Button variant='outline' size='sm' className='srd:rounded-full' onClick={onRetry}>
                Coba lagi
              </Button>
            )}
          </div>
        ) : (
          <>
            <InputGroup className='srd:w-full srd:shrink-0 srd:rounded-full srd:data-[size=default]:h-8'>
              <InputGroupAddon align='inline-start'>
                <Search className='srd:text-blue-100 srd:size-4' />
              </InputGroupAddon>
              <InputGroupInput
                placeholder='Cari'
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                type='search'
              />
            </InputGroup>

            {state === 'loading' ? (
              <div className='srd:flex srd:w-full srd:shrink-0 srd:flex-col srd:gap-y-2'>
                {[0, 1, 2, 3].map((index) => (
                  <Skeleton key={index} className='srd:h-16 srd:w-full srd:rounded-lg' />
                ))}
              </div>
            ) : (
              /*
                `flex-1` lets the list absorb the leftover height rather than pushing the
                footer past the dialog's cap, and the `min-h` floor keeps it from
                collapsing to 0px on short viewports (a scroll container's automatic
                minimum size is 0).
              */
              <div className='srd:flex srd:min-h-32 srd:w-full srd:min-w-0 srd:flex-1 srd:flex-col srd:overflow-x-hidden srd:overflow-y-auto'>
                {filtered.length === 0 ? (
                  <p
                    className={cn(
                      typographyVariants({ variant: 'body-sm' }),
                      'srd:text-grey-100 srd:py-6 srd:text-center',
                    )}
                  >
                    {search ? 'Tidak ada nama yang cocok dengan pencarian.' : 'Tidak ada penerima.'}
                  </p>
                ) : (
                  filtered.map((recipient) => <RecipientReadRow key={recipient.id} recipient={recipient} />)
                )}
              </div>
            )}
          </>
        )}

        {/*
          No `mx-0`/`mb-0` override: `DialogFooter` carries `-mx-6 -mb-6` so its bar bleeds
          to the dialog edges. `rounded-b-2xl` matches this dialog's radius.
        */}
        <DialogFooter className='srd:shrink-0 srd:flex-row srd:items-center srd:justify-end srd:rounded-b-2xl'>
          <Button variant='outline' className='srd:rounded-full' onClick={() => onOpenChange(false)}>
            Kembali
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Connected component
// ---------------------------------------------------------------------------

export interface ShareRecipientsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The sender's own (ACTOR) card. Supplies the header; the query supplies the list. */
  notification: NotificationView
}

/**
 * Read status of every recipient of one share, for the sender. `notification.recipients`
 * rejects a caller who holds no ACTOR row on the event, so this component only mirrors
 * a check that is enforced server-side.
 */
export function ShareRecipientsDialog({ open, onOpenChange, notification }: ShareRecipientsDialogProps) {
  const trpc = useSharedTRPC()
  const [search, setSearch] = React.useState('')

  // Keyed on `eventId`, not the notification id: the recipient set belongs to the event.
  const recipientsQuery = useQuery({
    ...trpc.notification.recipients.queryOptions({ eventId: notification.eventId }),
    enabled: open,
  })

  const recipients: ShareRecipientReadStatus[] = (recipientsQuery.data?.recipients ?? []).map((recipient) => ({
    id: recipient.id,
    name: recipient.name,
    image: recipient.image,
    schoolClassName: recipient.schoolClassName,
    isRead: recipient.readAt !== null,
  }))

  const state: ShareRecipientsDialogState = recipientsQuery.isError
    ? 'error'
    : recipientsQuery.isPending
      ? 'loading'
      : 'ready'

  return (
    <ShareRecipientsDialogBase
      open={open}
      onOpenChange={onOpenChange}
      cover={coverFromNotification(notification)}
      recipientCount={notification.recipientCount}
      createdAt={notification.createdAt}
      state={state}
      recipients={recipients}
      search={search}
      onSearchChange={setSearch}
      onRetry={() => void recipientsQuery.refetch()}
    />
  )
}
